/** Company and startup: dashboard, organisation profile and team, and (startups) co-founder search. */
import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html, safeUrl } from '../../core/html.js';
import { ago, fmtDate } from '../../core/format.js';
import { session } from '../../core/session.js';
import { reload, update } from '../../app/shell.js';
import { banner, chips, empty, ic, pageHead, stat, statusTag, table, tag, who } from '../../ui/components.js';
import { field, selectField } from '../../ui/forms.js';
import { closeModal, confirmBox, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';

const ROLES = ['company', 'startup'];
const ORG_ROLES = ['Admin', 'Recruiter', 'Hiring manager'];
const STAGES = ['Shortlisted', 'Interview', 'Offer', 'Hired'];
const forRoles = (make) => ROLES.map(make);

/* ---------- dashboard ---------- */

function dashboardRoute(role) {
  return {
    path: `/${role}/dashboard`,
    roles: [role],
    title: 'Dashboard',
    async load(ctx) {
      const [dash, list] = await Promise.all([
        api.get('/org/dashboard', { signal: ctx.signal }),
        api.get('/org/requirements', { signal: ctx.signal }),
      ]);
      return { ...dash, list: list.requirements };
    },
    view(d) {
      const r = d.requirements;
      const recent = d.list.slice(0, 4);
      return html`${pageHead(d.organization.name, role === 'company' ? 'Hire verified, trained candidates.' : 'Find the people to build with.', html`<a class="btn" href="/${role}/requirements/new">${ic('plus')} Post requirement</a>`)}
        ${d.organization.verified ? '' : banner('Your organisation is not verified yet. Vignova will check your details soon.', 'wn', 'shield')}
        <div class="grid stats">${stat(r.open, 'Open requirements')}${stat(r.in_review, 'In review')}${stat(d.matched_profiles, 'Matched profiles')}${stat(d.applications_new, 'New applications')}</div>
        <div class="grid g2 mt" style="align-items:start">
          <div class="card"><h3>Hiring pipeline</h3>${STAGES.map((s) => html`<div class="li" style="padding:6px 0"><span class="f1">${s}</span><b>${d.pipeline[s] || 0}</b></div>`)}
            <a class="btn ghost sm mt-s" href="/${role}/shortlisted">Open pipeline</a></div>
          <div class="card"><h3>Recent requirements</h3>${recent.length
            ? html`<div class="list sm">${recent.map((q) => html`<div class="li"><span class="f1"><a href="/${role}/requirements/${q.id}">${q.title}</a><br><span class="xs mu">${q.type}, ${fmtDate(q.created_at)}</span></span>${statusTag(q.status)}</div>`)}</div>`
            : html`<p class="mu">Post your first requirement to get started.</p>`}</div>
        </div>`;
    },
  };
}

/* ---------- profile + team ---------- */

function profileRoute(role) {
  return {
    path: `/${role}/profile`,
    roles: [role],
    title: role === 'company' ? 'Company profile' : 'Startup profile',
    async load(ctx) {
      const [p, team] = await Promise.all([api.get('/org/profile', { signal: ctx.signal }), api.get('/org/team', { signal: ctx.signal })]);
      return { org: p.organization, yourRole: p.your_role, members: team.members, invitations: team.invitations };
    },
    view(d) {
      const o = d.org;
      const admin = d.yourRole === 'Admin';
      return html`${pageHead(o.name, `${role === 'company' ? 'Company' : 'Startup'} profile. Candidates and Vignova see this.`, o.verified ? tag('Verified', 'ok') : tag('Not verified', 'wn'))}
        <div class="grid g2" style="align-items:start">
          <div class="card"><h3>About</h3>${admin ? '' : banner('Only an organisation admin can edit these details.', '', 'info')}
            <form data-submit="org.save" novalidate>
              <div class="fgrid">${field('og-ind', 'Industry', { name: 'industry', val: o.industry, req: false, maxlength: 80, disabled: !admin })}${field('og-city', 'City', { name: 'city', val: o.city, req: false, maxlength: 80, disabled: !admin })}
              ${field('og-size', 'Team size', { type: 'number', name: 'size', min: 1, max: 1000000, val: o.size ?? '', req: false, disabled: !admin })}${role === 'startup' ? field('og-stage', 'Stage', { name: 'stage', val: o.stage, req: false, maxlength: 60, ph: 'Idea, MVP, Seed', disabled: !admin }) : ''}</div>
              ${field('og-web', 'Website', { name: 'website', type: 'url', val: o.website, req: false, ph: 'https://', disabled: !admin })}
              ${field('og-about', 'About', { name: 'about', rows: 4, val: o.about, req: false, max: 3000, disabled: !admin })}
              ${field('og-look', 'Looking for', { name: 'looking_for', val: o.looking_for, req: false, maxlength: 200, ph: 'Junior QA engineers, a backend co-founder', disabled: !admin })}
              ${admin ? html`<button class="btn" type="submit">Save profile</button>` : ''}</form>
            ${o.website ? html`<p class="sm mt-s"><a href="${safeUrl(o.website)}" target="_blank" rel="noopener noreferrer">${ic('link')} ${o.website}</a></p>` : ''}</div>
          <div class="card"><h3>Team</h3>
            <div class="list sm">${d.members.map((m) => html`<div class="li">${who(m.name, m.email)}<span class="f1"></span>${tag(m.org_role, m.id === session.user.id ? 'ok' : 'n')}</div>`)}</div>
            ${d.invitations.length ? html`<h4 class="mt">Pending invitations</h4><div class="list sm">${d.invitations.map((i) => html`<div class="li"><span class="f1">${i.email}<br><span class="xs mu">${i.org_role}, expires ${fmtDate(i.expires_at)}</span></span>
              ${admin ? html`<button type="button" class="btn ghost sm" data-act="team.revoke" data-id="${i.id}" data-email="${i.email}">Revoke</button>` : ''}</div>`)}</div>` : ''}
            ${admin ? html`<h4 class="mt">Invite a teammate</h4><form data-submit="team.invite" novalidate><div class="fgrid">${field('iv-email', 'Work email', { name: 'email', type: 'email' })}${selectField('iv-role', 'Role', ORG_ROLES.map((r) => [r, r]), { name: 'org_role', val: 'Recruiter' })}</div>
              <button class="btn" type="submit">${ic('send')} Send invitation</button></form><p class="note mt-s">They get an email link that works for 7 days.</p>` : ''}</div>
        </div>`;
    },
  };
}

register({
  'org.save': (form) => submitForm(form, async (v) => {
    const body = {};
    for (const key of ['industry', 'city', 'stage', 'about', 'looking_for']) if (key in v) body[key] = v[key];
    if (v.website) body.website = v.website; // an empty website is left unchanged: the API needs a valid URL
    if (v.size !== '' && v.size != null) body.size = Number(v.size);
    await api.put('/org/profile', { body });
    toast('Profile saved');
    await reload();
  }),
  'team.invite': (form) => submitForm(form, async ({ email, org_role: orgRole }) => {
    await api.post('/org/team/invitations', { body: { email, org_role: orgRole } });
    form.reset();
    toast(`Invitation sent to ${email}`, 'send');
    await reload();
  }),
  'team.revoke': async (el) => {
    if (!(await confirmBox({ title: `Revoke invitation for ${el.dataset.email}?`, text: 'The link in their email stops working.', label: 'Revoke', danger: true }))) return;
    await api.delete(`/org/team/invitations/${el.dataset.id}`);
    await reload();
  },
});

/* ---------- startups: co-founders ---------- */

const cofounder = {
  path: '/startup/cofounder',
  roles: ['startup'],
  title: 'Co-founders',
  ui: () => ({ skill: '' }),
  async load(ctx) {
    const [people, intros] = await Promise.all([
      api.get('/org/cofounders', { signal: ctx.signal }),
      api.get('/org/intros', { signal: ctx.signal }),
    ]);
    return { people: people.cofounders, intros: intros.intros };
  },
  view(d, ui) {
    const asked = new Set(d.intros.filter((i) => i.status !== 'declined').map((i) => i.cofounder));
    const list = d.people.filter((p) => !ui.skill || p.skills.some((s) => s.toLowerCase().includes(ui.skill.trim().toLowerCase())));
    return html`${pageHead('Find a co-founder', 'People who chose to be open to introductions. You cannot message them directly: Vignova introduces you, and they choose whether to accept.')}
      <div class="row mb"><input style="width:260px" placeholder="Filter by skill" aria-label="Filter by skill" value="${ui.skill}" data-input="cofounder.filter"></div>
      ${list.length
        ? html`<div class="grid g3">${list.map((p) => html`<div class="card">${who(p.name, p.mode)}<p class="sm mt-s">${p.bio || ''}</p>${chips(p.skills)}
          <div class="mt-s">${asked.has(p.name) ? tag('Introduction requested', 'wn') : html`<button type="button" class="btn soft sm" data-act="cofounder.intro" data-id="${p.user_id}" data-name="${p.name}">Request introduction</button>`}</div></div>`)}</div>`
        : empty('rocket', 'No one matches yet', 'Try a different skill, or check back as more people opt in.')}
      ${d.intros.length ? html`<h3 class="mt">Your introduction requests</h3>${table(['Person', 'Status', 'Requested', ''], d.intros.map((i) => [i.cofounder, statusTag(i.status), ago(i.at), i.conversation_id ? html`<a class="btn sm" href="/startup/messages?c=${i.conversation_id}">${ic('chat')} Message</a>` : '']))}` : ''}`;
  },
};

register({
  'cofounder.filter': (el) => update((ui) => { ui.skill = el.value; }),
  'cofounder.intro': (el) => openModal({
    title: `Introduction to ${el.dataset.name}`,
    body: html`<p>Vignova reviews the request and, if it fits, forwards it. ${el.dataset.name} decides whether to talk to you.</p>
      <form id="intro-form" data-submit="cofounder.intro.send" data-id="${el.dataset.id}" novalidate>${field('in-note', 'What you are building and why them', { name: 'note', rows: 5, minlen: 10, max: 1000, err: 'Write at least 10 characters.' })}</form>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="intro-form" class="btn">Request introduction</button>`,
  }),
  'cofounder.intro.send': (form) => submitForm(form, async ({ note }) => {
    await api.post('/org/intros', { body: { cofounder_user_id: Number(form.dataset.id), note } });
    closeModal(false);
    toast('Introduction requested. Vignova will review it.', 'send');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
});

export const routes = [...forRoles(dashboardRoute), ...forRoles(profileRoute), cofounder];
